import { Card, CardContent, Typography, Box, SxProps, Theme } from '@mui/material';

import { AppIcon, ICON_SIZES } from '../designSystem/icons/AppIcon';
import { Tool } from '../models/tool';

const STICKER_DIAMETER = 72;

export type ActionStickerCardProps = Readonly<{
  title: string;
  description?: string;
  stickerUrl: string;
  onClick: () => void;
  tool?: Tool;
  showArrow?: boolean;
}>;

export function ActionStickerCard({
  title,
  description,
  stickerUrl,
  onClick,
  showArrow = true,
}: ActionStickerCardProps) {
  return (
    <Card elevation={0} sx={containerSx} onClick={onClick}>
      <Box sx={cardActionAreaSx}>
        {/* STICKER AND TOP RIGHT ICON (SMALL SCREEN ONLY FOR ICON) */}

        <Box sx={stickerSx} component="img" src={stickerUrl} alt="Sticker" />

        {/* TITLE AND DESCRIPTION */}
        <CardContent sx={cardTextSx}>
          <Box sx={titleIconContainerSx}>
            <Typography variant="text-md-semibold" sx={titleSx(!!description)}>
              {title}
            </Typography>
            {showArrow && (
              <Box sx={iconContainerSxSmallScreen}>
                <AppIcon
                  iconName="ArrowCircleUpRight"
                  color="Colors/Base/Black"
                  diameter="24px"
                  strokeWidth="thin"
                />
              </Box>
            )}
          </Box>
          {description && (
            <Typography variant="text-sm-regular" sx={descriptionSx}>
              {description}
            </Typography>
          )}
        </CardContent>
      </Box>
    </Card>
  );
}

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  userSelect: 'none',
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-xl'],
  px: backrs.spacing['spacing-xl'],
  py: backrs.spacing['spacing-2xl'],
  maxWidth: { xs: '100%', sm: '343px' },
  minHeight: '114px',
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: backrs.shadows['shadow-xl'],
    transform: 'translate(-2px, -2px)',
  },
  '&:active': {
    outline: 'none',
    transform: 'translate(0px, 0px)',
    boxShadow: 'none',
    transition: 'all 0s',
  },
});

const cardTextSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  p: backrs.spacing['spacing-none'],
  gap: backrs.spacing['spacing-none'],
  marginLeft: backrs.spacing['spacing-sm'],
  alignSelf: 'flex-start',
  flexGrow: {
    xs: 1,
    sm: 'unset',
  },
  paddingBottom: `${backrs.spacing['spacing-none']} !important`,
});

const cardActionAreaSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: {
    xs: 'row',
    sm: 'row',
  },
  alignItems: {
    xs: 'flex-start',
    sm: 'center',
  },
  gap: { xs: backrs.spacing['spacing-sm'] },
  textDecoration: 'none',
});

const stickerSx: SxProps<Theme> = {
  display: 'flex',
  alignSelf: 'flex-start',
  width: {
    xs: STICKER_DIAMETER * 0.75,
    sm: STICKER_DIAMETER,
  },
  height: {
    xs: STICKER_DIAMETER * 0.75,
    sm: STICKER_DIAMETER,
  },
  minWidth: {
    xs: STICKER_DIAMETER * 0.75,
    sm: STICKER_DIAMETER,
  },
  minHeight: {
    xs: STICKER_DIAMETER * 0.75,
    sm: STICKER_DIAMETER,
  },
  overflow: 'hidden',
  '& img': {
    maxWidth: '500%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
};

const iconContainerSxSmallScreen: SxProps<Theme> = {
  cursor: 'pointer',
  height: ICON_SIZES['24px'],
};

const titleIconContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  width: { xs: '100%', sm: 'unset' },
  justifyContent: 'space-between',
};

const titleSx =
  (hasDescription: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: backrs.colors['Colors/Text/text-primary'],
    alignSelf: {
      xs: 'flex-end',
      sm: 'unset',
    },
    ...(!hasDescription && {
      marginTop: backrs.spacing['spacing-xl'],
    }),
    lineHeight: '1.25rem',
  });

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  pr: backrs.spacing['spacing-xl'],
});
